import { Alert, Button, Content } from "@patternfly/react-core";
import { Modal } from "@patternfly/react-core/deprecated";
import "./Modal.scss";
import { ErrorPopup, SuccessPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { FormContainer } from "@rhc-shared-components/form";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import { isEmpty } from "lodash";
import type { FC } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { patchProductListing } from "../../../api/pyxis";
import { ProductKeys } from "../../../constants/product";
import type { TProductDetails } from "../../../hooks/useProductMetadata";
import type { IPyxisAPIError } from "../../../interfaces/pyxis";
import type { ProductListing } from "../../../types/generated-graphql";
import Env from "../../../utils/env";

interface DeleteProductProps {
	handleDeleteModalOk?: () => void;
	handleDeleteModalCancel: () => void;
	productDetail: TProductDetails;
	certificationList: { _id: string }[];
}

export const DeleteProductListingModal: FC<DeleteProductProps> = ({
	productDetail,
	certificationList: certificationListState,
	handleDeleteModalCancel,
	handleDeleteModalOk,
}: DeleteProductProps) => {
	const { setAlertState } = useAlertContext();
	const history = useHistory();
	const deleteProductListing = useMutation({
		mutationFn: (request: Partial<ProductListing>) => patchProductListing(request),
		onSuccess: async () => {
			SuccessPopup(setAlertState, `Deleted "${productDetail[ProductKeys.productName]}"`);
			handleDeleteModalOk?.();
			history.push(`${Env.routePrefix}/products`);
		},
		onError: (error: IPyxisAPIError) => {
			if (error.status === 403) {
				ErrorPopup(setAlertState, error.detail);
			} else {
				ErrorPopup(setAlertState, error.detail);
			}
		},
	});
	return (
		<Formik
			initialValues={{ delete: "" }}
			validationSchema={() => {
				return Yup.object().shape({
					delete: Yup.string()
						.required("Please type DELETE in the input box to confirm")
						.test("is-delete", "Please type DELETE in the input box to confirm", (value: string) => {
							return value === "DELETE";
						}),
				});
			}}
			onSubmit={async () => {
				const requestData = {
					[ProductKeys.id]: productDetail[ProductKeys.id],
					[ProductKeys.certProjects]: [],
					[ProductKeys.deleted]: true,
				};
				await deleteProductListing.mutateAsync(requestData);
			}}
			enableReinitialize
		>
			{(formikProps) => (
				<Modal
					title="Delete Product Listing"
					width="35%"
					id="shared-modal"
					className="modal-style"
					isOpen
					onClose={handleDeleteModalCancel}
					actions={[
						<Button
							variant="danger"
							key="delete"
							isLoading={formikProps.isSubmitting || deleteProductListing.isPending}
							isDisabled={
								!(formikProps.touched && formikProps.dirty && isEmpty(formikProps.errors)) ||
								formikProps.isSubmitting ||
								deleteProductListing.isPending
							}
							onClick={() => {
								formikProps.handleSubmit();
							}}
						>
							Delete
						</Button>,
						<Button
							key="cancel"
							variant="link"
							isDisabled={deleteProductListing.isPending}
							onClick={handleDeleteModalCancel}
						>
							Cancel
						</Button>,
					]}
				>
					<FormContainer gridSpan={12}>
						<Content component="p">
							Deleting {productDetail[ProductKeys.productName]} will remove it from your account, making it
							inaccessible.{" "}
						</Content>
						{certificationListState.length >= 1 && (
							<Alert
								className="project-detach-alert"
								isInline
								variant="warning"
								title="There are certification projects attached to this product listing."
							/>
						)}
						<FormTextInput
							label={
								certificationListState.length >= 1
									? "Type DELETE to detach them and proceed with product listing deletion."
									: "Type DELETE to confirm"
							}
							name="delete"
						/>
					</FormContainer>
				</Modal>
			)}
		</Formik>
	);
};
